<template>
  <template v-for="menu in menusDataWithActiveMenuItem" :key="menu.id">
    <div v-show="menu.id === menuData.activeMenu" class="iorder-menu-list">
      <div class="container iorder-menu-list__container">
        <div v-if="menu.menuTree" class="iorder-menu-list__header">
          <h2 class="iorder-menu-list__heading" :class="headingClasses">
            {{ menu.name }}
          </h2>
        </div>
        <div
          v-for="(category, index) in menu.menuTree"
          :id="String(category.id)"
          :key="category.id"
          :ref="(el) => menu.id === menuData.activeMenu && addRef(el, index)"
          :data-menu-id="menu.id"
          class="iorder-menu-list__listing"
        >
          <div class="iorder-menu-list-separator">
            <div v-if="isIconSeparator" class="iorder-menu-list-separator__icon">
              <i class="icon" :class="[seperatorIconClass]" aria-hidden="true"></i>
            </div>
            <div class="iorder-menu-list-separator__container">
              <h3 class="iorder-menu-list-separator__heading">
                {{ category.name }}
                <template v-if="category.children?.some(isProduct)">
                  ({{ category.children.filter(isProduct).length }})
                </template>
              </h3>
              <p v-if="menu.description" class="iorder-menu-list-separator__desc">
                {{ menu.description }}
              </p>
            </div>
          </div>
          <div
            v-if="category.children?.length"
            class="iorder-menu-list__content"
            :class="{ 'iorder-menu-list__content--list': isListView }"
          >
            <template v-for="child in category.children" :key="child.id">
              <item-text-divider
                v-if="
                  isItemType(child, MenuChildType.SUBHEADER) ||
                  isItemType(child, MenuChildType.TEXTFIELD)
                "
                :text="child.text"
                :type="child.type"
              />
              <menu-item-image
                v-else-if="isItemType(child, MenuChildType.IMAGE) && !isListView"
                :image="child.imageUrl"
                :alt-text="category.name"
              />
              <menu-item
                v-else-if="isProduct(child)"
                :product="child"
                :show-calories="showCalories"
                :list-view="isListView"
              />
            </template>
            <menu-item-deal
              v-if="deals?.value?.length && !isListView"
              :deals="deals"
              :menu-name="menu.name"
              :category-name="category.name"
            />
          </div>
          <p v-else class="iorder-menu-list__no-results">
            {{ t('iorder-filters-no-results') }}
          </p>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import type { MenuListProps } from './types';
import type { MenuData, MenuFilter, MenuFilterValue, DealCard, SitecoreField } from '../types';
import { type MenuProduct, MenuChildType } from '@/types/menu';
import {
  type ComponentPublicInstance,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useThrottleFn } from '@vueuse/core';
import MenuItem from '../MenuItem/MenuItem.vue';
import MenuItemImage from '../MenuItemImage/MenuItemImage.vue';
import ItemTextDivider from '../ItemTextDivider/ItemTextDivider.vue';
import MenuItemDeal from '../MenuItemDeal/MenuItemDeal.vue';
import { useJssStore } from '../../../stores/jssStore';

const isItemType = <T extends MenuChildType>(
  child: MenuProduct,
  type: T
): child is MenuProduct & { type: T } => child.type === type;
const isProduct = (child: MenuProduct) => isItemType(child, MenuChildType.PRODUCT);

const { t } = useI18n();

const props = withDefaults(defineProps<MenuListProps>(), {
  menuData: () => ({}) as MenuData,
  navRef: null,
  separatorsType: () => ({}) as SitecoreField,
  deals: () => ({ value: [] }) as SitecoreField<DealCard[]>,
  layout: 'grid',
});

const jssStore = useJssStore();

const emit = defineEmits<{
  changeActiveItem: [id: number];
  scrolledToActiveItem: [id: number];
  onAvailableFiltersCollected: [];
}>();

const isMounted = ref(false);
const activeMenuCategories = ref<Element[]>([]);

const menusDataWithActiveMenuItem = computed(() =>
  props.menuData.menus.map((menu) => {
    if (menu.id === props.menuData.activeMenu) return props.menuData.activeMenuItem;
    return menu;
  })
);

const isListView = computed(() => props.layout === 'list');

const showCalories = computed(
  () =>
    props.menuData?.activeFilters
      ?.find((f: MenuFilter) => f.name === 'Others')
      ?.values?.find((v: MenuFilterValue) => v.name === 'Show calories')?.enabled
);

const headingClasses = computed(() => [
  ['HungryHorse', 'Belhaven', 'Everly', 'FamhouseKitchen'].includes(jssStore.brandName)
    ? 'font-xl'
    : 'font-l',
]);

const isIconSeparator = computed(() => props.separatorsType?.value?.toLowerCase() !== 'text');

const seperatorIconClass = computed(() => {
  if (props.menuData.activeMenuItem?.tags?.includes('FOOD')) {
    return 'icon-Cutlry';
  }
  if (props.menuData.activeMenuItem?.tags?.includes('DRINK')) {
    return 'icon-Wine-Glass';
  }
  return '';
});

const setActiveCategory = useThrottleFn(() => {
  if (!activeMenuCategories.value.length) return;

  const active = { position: Number.NEGATIVE_INFINITY, id: null as number | null };
  const smallest = { position: Number.POSITIVE_INFINITY, id: null as number | null };
  const navBoundingRect = props.navRef?.getBoundingClientRect();
  const navPosition = navBoundingRect?.bottom ?? 0;
  activeMenuCategories.value.forEach((category) => {
    const position = category.getBoundingClientRect().top - navPosition - 30;
    if (position <= 0 && position > active.position) {
      active.position = position;
      active.id = +(category.getAttribute('id') ?? 0);
    }
    if (position < smallest.position) {
      smallest.position = position;
      smallest.id = +(category.getAttribute('id') ?? 0);
    }
  });

  if (active.position === Number.NEGATIVE_INFINITY && !!activeMenuCategories.value.length)
    active.id = smallest.id;

  if (
    active.id &&
    active.id === props.menuData?.activeSubmenu &&
    active.position !== Number.NEGATIVE_INFINITY
  )
    emit('scrolledToActiveItem', active.id);
  if (active.id && active.id !== props.menuData?.activeSubmenu) emit('changeActiveItem', active.id);
}, 30);

const addRef = (el: Element | ComponentPublicInstance | null, index: number) => {
  if (el instanceof Element) activeMenuCategories.value[index] = el;
};

const onScrollResize = () => {
  (async () => {
    await setActiveCategory();
  })();
};

watch(
  () => props.menuData?.activeMenuItem?.id,
  () => {
    activeMenuCategories.value = [];
  }
);

onMounted(() => {
  isMounted.value = true;
  document.addEventListener('scroll', onScrollResize);
  window.addEventListener('resize', onScrollResize);
});

onBeforeUnmount(() => {
  document.removeEventListener('scroll', onScrollResize);
  window.removeEventListener('resize', onScrollResize);
});
</script>

<style lang="scss">
@import './scss/MenuList.scss';
</style>
